// NotificationCenter — bell icon + dropdown notification panel.
//
// Mount points:
//   AppHeader (variant="header") — dropdown, absolute-positioned below bell
//   CourseSidebar footer (variant="sidebar") — portal-rendered, fixed-positioned
//     to escape the sidebar's overflow-y: auto clipping container.
//
// For sidebar variant, the portal host div is created once on mount (via
// useState lazy initializer) so it's always ready before the first open.
// This avoids the useRef→useEffect timing gap where the panel would render
// inline and get clipped by the sidebar's overflow.
//
// Reads from Zustand notification slice. Each notification flows through:
//   useNotify() → pushNotification() + EventBus → Toast bridge + this component.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from '../lib/store/index.js';
import type {  MessageFilter } from '@examforge/shared';
import { NotificationBell } from './notification-bell.js';
import { NotificationPanel } from './notification-panel.js';
import { useNotificationPosition } from './use-notification-position.js';

export interface NotificationCenterProps {
  className?: string;
  variant?: 'header' | 'sidebar';
}

export function NotificationCenter({ className, variant = 'header' }: NotificationCenterProps) {
  // ── Store ─────────────────────────────────────────────────────────────
  const notifications = useStore((s) => s.notifications);
  const unreadCount = useStore((s) => s.unreadCount);
  const markRead = useStore((s) => s.markRead);
  const markAllRead = useStore((s) => s.markAllRead);
  const dismiss = useStore((s) => s.dismiss);
  const clearAll = useStore((s) => s.clearAll);

  // ── Local state ───────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<MessageFilter>('all');
  const btnRef = useRef<HTMLButtonElement>(null!);
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Portal host — re-attached on every effect run ──────────────────────
  //    In React StrictMode (dev), effects run mount → unmount → remount.
  //    If we only create the node in useState's lazy init, the cleanup
  //    removes it from body during the simulated unmount, and the remount
  //    never re-adds it → createPortal renders into a detached node → invisible.
  //    Fix: effect body re-attaches the node if it's been removed.
  const [portalNode] = useState<HTMLDivElement | null>(() => {
    if (variant === 'sidebar') {
      const el = document.createElement('div');
      el.className = 'notif-portal-host';
      document.body.appendChild(el);
      return el;
    }
    return null;
  });

  // Ensure portal node stays attached (re-add after StrictMode cleanup).
  // Use portalNode.remove() — safe on already-detached nodes, avoids the
  // removeChild NotFoundError that crashed the ErrorBoundary in StrictMode.
  useEffect(() => {
    if (portalNode && !portalNode.parentNode) {
      document.body.appendChild(portalNode);
    }
    return () => {
      if (portalNode) {
        try { portalNode.remove(); } catch { /* already detached */ }
      }
    };
  }, [portalNode]);

  // ── Position calculation (for portal variant) ─────────────────────────
  const position = useNotificationPosition(btnRef, open, variant);

  // ── Keyboard: Esc closes ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // ── Outside click closes ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current && panelRef.current.contains(target)) return;
      if (btnRef.current && btnRef.current.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleMarkRead = useCallback((id: string) => markRead(id), [markRead]);
  const handleDismiss = useCallback((id: string) => dismiss(id), [dismiss]);

  // ── Render ────────────────────────────────────────────────────────────
  const panel = open && (
    <NotificationPanel
      notifications={notifications}
      unreadCount={unreadCount}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onMarkRead={handleMarkRead}
      onMarkAllRead={markAllRead}
      onDismiss={handleDismiss}
      onClearAll={clearAll}
      onClose={() => setOpen(false)}
      panelRef={panelRef}
      position={position}
      variant={variant}
    />
  );

  return (
    <div className={`notif-center${className ? ` ${className}` : ''}`}>
      <NotificationBell
        unreadCount={unreadCount}
        open={open}
        btnRef={btnRef}
        onClick={() => setOpen((v) => !v)}
      />

      {variant === 'sidebar' && panel && portalNode
        ? createPortal(panel, portalNode)
        : panel}
    </div>
  );
}
